import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Avatar,
  TableRow,
  Paper,
  Pagination,
  Menu,
  MenuItem,
  Rating,
  useTheme,
} from "@mui/material";
import { Search, MoreVert } from "@mui/icons-material";

import { PATH_DASHBOARD } from "@app/routes/paths";
import { FormProvider, RHFTextField } from "@app/components";
import { Strings } from "@app/constants";
import { useRequestState } from "@app/hooks";
import { allCars, deleteProductById, product } from "../slice";
import { selectAllCarsDataState, selectDeleteProductState } from "../selector";
import { AssignCustomerModal } from "@app/modules/salesperson/view/assign-customer-modal";
import { SoldCarModal } from "./soldCar";

const Product = () => {
  const methods = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const itemsPerPage = 8;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  useRequestState({
    stateSelector: selectDeleteProductState,
    successMessageShown: true,
    errorShown: true,
  });
  const { data: allProductDataView } = useRequestState({
    stateSelector: selectAllCarsDataState,
    errorShown: true,
  });
  useEffect(() => {
    dispatch(allCars());
  }, [dispatch]);
  const displayedProducts = Array.isArray(allProductDataView)
    ? allProductDataView
    : [];

  const filteredData = displayedProducts?.filter((item) => {
    const searchTerm = search.toLowerCase();

    const includes = (field: any) =>
      String(field || "")
        .toLowerCase()
        .includes(searchTerm);

    return (
      includes(item?.brand?.name) ||
      includes(item?.model?.name) ||
      includes(item?.transmission) ||
      includes(item?.engineType) ||
      includes(item?.manufacturingYear) ||
      includes(item?.driven) ||
      includes(item?.price) ||
      includes(item?.name) ||
      includes(item?.category?.name) ||
      includes(item?.avgRating)
    );
  });

  const totalFilteredItems = filteredData.length;
  const paginatedData = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    dispatch(
      product({
        limit: 100,
        page: 1,
        search: "",
      })
    );
  }, [dispatch]);

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" sx={{ minHeight: "100vh", bgcolor: "#F5F5F5" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap={{ xs: "wrap", sm: "nowrap" }}
              rowGap={2}
              mb={3}
            >
              <RHFTextField
                name="Search"
                placeholder="Search"
                size="small"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <Button
                  variant="contained"
                  sx={{
                    width: 150,
                    textTransform: "none",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => navigate(PATH_DASHBOARD.addProduct1)}
                >
                  {Strings.button.addCar}
                </Button>
              </Stack>
            </Stack>

            <Stack
              sx={{
                width: { xs: "calc(100vw - 48px)", sm: "calc(100vw - 288px)" },
                overflow: "hidden",
              }}
            >
              <TableContainer component={Paper} sx={{ pb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[
                        "Image",
                        "Brand",
                        "Model",
                        "Transmission Type",
                        "Manufacturing Year",
                        "Engine Type",
                        "Car Travelled",
                        "Price",
                        "Rating",
                        "Action",
                      ].map((heading: any) => (
                        <TableCell
                          key={heading}
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Poppins",
                            color: "#474747",
                            minWidth: [
                              "Transmission Type",
                              "Manufacturing Year",
                              "Engine Type",
                              "Car Travelled",
                            ].includes(heading)
                              ? 200
                              : 110,
                          }}
                        >
                          {heading}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((item: any, index: any) => (
                        <TableRow
                          key={item.id || index}
                          onClick={() => {
                            handleMenuClose();
                            navigate(`${PATH_DASHBOARD.productDetail}`, {
                              state: { productId: item.id },
                            });
                          }}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "#E3F2FD",
                            },
                            "&.Mui-selected, &.Mui-selected:hover": {
                              backgroundColor: "#BBDEFB",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                              pl: "4",
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                right: 12,
                                fontSize: 14,
                              }}
                              src={
                                item?.images?.[0]?.url ||
                                "https://randomuser.me/api/portraits/men/75.jpg"
                              }
                            >
                              {item.name?.charAt(0)?.toUpperCase() || "?"}
                            </Avatar>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item.brand.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item.model.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item.transmission}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item.manufacturingYear}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item.engineType}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item.driven}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            {item?.price?.toLocaleString("en-IN", {
                              currency: "INR",
                              style: "currency",
                              maximumFractionDigits: 0,
                            })}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              color: "#5F5F5F",
                            }}
                          >
                            <Rating
                              name={`rating-${item.id}`}
                              value={Number(item.avgRating) || 0}
                              precision={0.5} // allows half stars if needed
                              readOnly
                              size="small"
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, item.id);
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontFamily: "Poppins",
                            color: "#474747",
                          }}
                        >
                          {search
                            ? "No Cars found matching your search"
                            : "No Cars found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalFilteredItems > 0 && (
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={2}
                  mt={2}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontFamily: fontFamily,
                      fontWeight: 400,
                    }}
                  >
                    {`Showing ${
                      filteredData?.length === 0
                        ? 0
                        : (page - 1) * itemsPerPage + 1
                    } to ${Math.min(
                      page * itemsPerPage,
                      filteredData?.length
                    )} of ${filteredData.length} Cars`}
                  </Typography>
                  <Pagination
                    count={Math.ceil(filteredData.length / itemsPerPage)}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    shape="rounded"
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(`${PATH_DASHBOARD.editProduct}`, {
              state: { productId: selectedRow },
            });
          }}
        >
          {Strings.button.edit}
        </MenuItem>
        {/* <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedRow !== null) {
              dispatch(deleteProductById({ productId: selectedRow }));
            }
          }}
        >
          {Strings.button.delete}
        </MenuItem> */}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            const rowData = paginatedData?.find(
              (item) => item?.id === selectedRow
            );
            setSelectedRowData(rowData);
            setOpenModal(true);
          }}
        >
          {Strings.button.sold}
        </MenuItem>
      </Menu>
      {openModal && (
        <SoldCarModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          selectedRow={selectedRowData}
        />
      )}
    </FormProvider>
  );
};

export { Product };
